// Écrans de consultation : études, tests utilisateurs, personas, audits,
// triangulation.
//
// Tous suivent le même contrat : chargement → erreur → vide → contenu. Ces
// quatre états sont écrits UNE fois (`ListSection`), parce que les oublier est la
// première cause d'écran blanc — et l'ancien frontend n'en gérait aucun,
// affichant simplement du vide quand une requête échouait.
import { savePreference } from '../legal/cookieConsent';
import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  useAudits,
  usePersonas,
  useStudies,
  useStudyPlan,
  useTests,
  useTriangulations,
} from '../api/queries';
import { StudiesRecommendations } from '../plan/Recommendations';
import { KINDS, protocolSize, protocolSummary, studyKind } from '../studies/kinds';
import '../studies/studies.css';
import { PersonaNeedsView, seniorityLabel } from '../personas/PersonaNeeds';
import { personaAside, personaLine, personaTitle } from '../personas/labels';
import type { Audit, Persona, Study, StudyKind, TriangulationRow, UserTest } from '../api/types';
import { Button, Chip, chipClass, chipState, EmptyState, ErrorBanner, Eyebrow, Note, Spinner, Avatar } from '../components/ui';
import { initialsOf } from '../components/AppShell';
import { Link, useRouter } from '../router';
import { presetTab } from '../components/Tabs';
import { useCanWrite } from '../write/useCanWrite';
import { Drawer } from '../write/Drawer';
import { StatusChip, StudyTypeChip, TagChip } from '../taxonomy/Chips';
import { KIND_STYLE } from '../taxonomy/colors';
import { Icon } from '../components/icons';
import { FilterNote, LifecycleChip, useProjectFilter } from '../lifecycle/ProjectFilter';
import {
  AuditForm,
  DeletePersonaAction,
  DeleteTestAction,
  EditAction,
  NewPersonaForm,
  NewTriangulationForm,
  TestForm,
  TriangulationRowForm,
  WriteAction,
} from '../write/Forms';

// --- Coquille commune aux cinq écrans -------------------------------------

interface SectionProps<T> {
  title: string;
  lede: string;
  query: UseQueryResult<T[]>;
  emptyTitle: string;
  emptyDescription: string;
  /** Boutons de saisie. Absents quand l'utilisateur n'a pas le droit d'écrire. */
  actions?: ReactNode;
  /** Filtre du projet (phase, version). Absent : tout est montré. */
  filter?: (item: T) => boolean;
  /** Bloc affiché sous l'en-tête, AVANT la liste — même quand elle est vide. */
  intro?: ReactNode;
  /** Barre juste au-dessus de la liste (filtres propres à l'écran). Masquée
   *  quand il n'y a encore rien à filtrer. */
  toolbar?: ReactNode;
  /** Onglets au-dessus de l'en-tête (« Utilisateurs et personas »). */
  tabs?: ReactNode;
  children: (items: T[]) => ReactNode;
}

function ListSection<T>({
  title,
  lede,
  query,
  emptyTitle,
  emptyDescription,
  actions,
  filter,
  intro,
  toolbar,
  tabs,
  children,
}: SectionProps<T>) {
  const items = query.data ? (filter ? query.data.filter(filter) : query.data) : [];
  return (
    <main className="screen">
      {tabs}
      <div className="zy-page-head__texte">
        <Eyebrow>{title}</Eyebrow>
        <h1 className="t-h1">{title}</h1>
        <p className="zy-intro">{lede}</p>
        {actions ? <div className="screen-actions">{actions}</div> : null}
      </div>

      {intro}

      {query.isPending ? (
        <div className="screen-loading">
          <Spinner label={`Chargement — ${title}`} />
        </div>
      ) : query.isError ? (
        <>
          <ErrorBanner>
            {query.error instanceof Error ? query.error.message : 'Chargement impossible.'}
          </ErrorBanner>
          <Button variant="ghost" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        </>
      ) : query.data.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <>
          {toolbar}
          <FilterNote shown={items.length} total={query.data.length} />
          {/* Un projet FILTRÉ jusqu'au vide n'est pas un projet vide : on ne
              montre pas l'invitation à créer, qui induirait en erreur. */}
          {items.length === 0 ? (
            <p className="zy-texte-muet">Rien ne correspond aux filtres.</p>
          ) : (
            children(items)
          )}
        </>
      )}
    </main>
  );
}

// --- Études ---------------------------------------------------------------

type KindFilter = 'all' | 'autre' | NonNullable<ReturnType<typeof studyKind>>;

/** Filtre par TYPE d'étude, mémorisé par projet (commodité, pas une donnée). */
function useKindFilter(projectId: string) {
  const key = `ro.studies.kind.${projectId}`;
  const [value, setValue] = useState<KindFilter>(() => {
    try {
      return (localStorage.getItem(key) as KindFilter | null) ?? 'all';
    } catch {
      return 'all';
    }
  });
  const set = (v: KindFilter) => {
    setValue(v);
    savePreference(key, v);
  };
  return [value, set] as const;
}

function kindOf(s: Study): KindFilter {
  return studyKind(s.type) ?? 'autre';
}

function KindFilterBar({ studies, value, onChange }: { studies: Study[]; value: KindFilter; onChange: (v: KindFilter) => void }) {
  const count = (k: KindFilter) => (k === 'all' ? studies.length : studies.filter((s) => kindOf(s) === k).length);
  const options: { key: KindFilter; label: string }[] = [
    { key: 'all', label: 'Toutes' },
    ...KINDS.map((k) => ({ key: k.kind as KindFilter, label: k.label })),
    ...(count('autre') > 0 ? [{ key: 'autre' as KindFilter, label: 'Autres' }] : []),
  ];
  return (
    <div className="st-filter" role="group" aria-label="Filtrer par type d’étude">
      {options.map((o) => {
        const n = count(o.key);
        const on = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            className={`st-filter-btn${on ? ' st-filter-on' : ''}${n === 0 && !on ? ' st-filter-empty' : ''}`}
            aria-pressed={on}
            onClick={() => onChange(o.key)}
          >
            {/* Le pictogramme du type, dans sa teinte : le même que sur les cartes. */}
            {o.key in KIND_STYLE ? (
              <span className={`st-filter-icon hue-${KIND_STYLE[o.key as StudyKind].hue}`}>
                <Icon name={KIND_STYLE[o.key as StudyKind].icon} size={13} />
              </span>
            ) : null}
            {o.label}
            <span className="st-filter-n">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

export function StudiesScreen({ projectId }: { projectId: string }) {
  const query = useStudies(projectId);
  const canWrite = useCanWrite(projectId);
  const { navigate } = useRouter();
  const filter = useProjectFilter();
  const plan = useStudyPlan(projectId);
  const [kind, setKind] = useKindFilter(projectId);
  // Le type se combine au filtre du projet (phase, version) : les compteurs
  // du filtre par type portent sur ce que le filtre du projet laisse voir.
  const visible = (query.data ?? []).filter(filter.matches);
  return (
    <ListSection<Study>
      title="Études"
      lede="Les expériences terrain menées sur ce projet — entretiens, observations, tests utilisateurs, questionnaires et audits UX."
      query={query}
      filter={(s) => filter.matches(s) && (kind === 'all' || kindOf(s) === kind)}
      toolbar={<KindFilterBar studies={visible} value={kind} onChange={setKind} />}
      actions={
        <>
          <StudiesRecommendations projectId={projectId} canWrite={canWrite} plan={plan.data} />
          {canWrite ? (
            <>
              {/* La construction d'une étude a sa page (055) : type, cadrage, protocole,
                  participants, créneaux, invitations. */}
              <Button variant="accent" onClick={() => navigate(`/projet/${encodeURIComponent(projectId)}/etudes/nouvelle`)}>
                Nouvelle étude
              </Button>
            </>
          ) : (
            <Note tone="lecture">Lecture seule — écriture réservée au responsable du projet</Note>
          )}
        </>
      }
      emptyTitle="Aucune étude pour l’instant"
      emptyDescription="Une étude cadre ce que vous cherchez à comprendre : objectif, cible, contexte. C’est elle qui rattache ensuite les verbatims à leur origine."
    >
      {(studies) => (
        <ul className="card-list">
          {studies.map((s) => (
            <li key={s.id} className="card">
              <div className="card-head">
                <div>
                  <Eyebrow>{s.code ?? s.id}</Eyebrow>
                  <h2 className="t-h3">
                    <Link
                      to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(s.id)}`}
                      className="card-title-link"
                    >
                      {s.objective ?? 'Sans objectif renseigné'}
                    </Link>
                  </h2>
                </div>
                {/* Le TYPE se lit avant le statut : il dit de quelle nature est
                    l'étude (entretiens, audit, questionnaire…), le statut dit
                    seulement où elle en est. */}
                <div className="card-head-right">
                  <StudyTypeChip type={s.type} />
                  <StatusChip value={s.status} />
                  <LifecycleChip projectId={projectId} kind="study" id={s.id} phase={s.phase} version={s.version} canWrite={canWrite} />
                </div>
              </div>
              <dl className="facts">
                {s.method ? (
                  <div>
                    <dt>Méthode</dt>
                    <dd>{s.method}</dd>
                  </div>
                ) : null}
                {s.target ? (
                  <div>
                    <dt>Cible</dt>
                    <dd>{s.target}</dd>
                  </div>
                ) : null}
                {s.participants != null ? (
                  <div>
                    <dt>Participants</dt>
                    <dd>{s.participants}</dd>
                  </div>
                ) : null}
                {s.period ? (
                  <div>
                    <dt>Période</dt>
                    <dd>{s.period}</dd>
                  </div>
                ) : null}
                {protocolSummary(s) ? (
                  <div>
                    <dt>Protocole</dt>
                    <dd className={`st-proto-chip${protocolSize(s.protocol) === 0 && !s.test_id && !s.audit_id ? ' st-proto-chip-todo' : ''}`}>
                      {protocolSummary(s)}
                    </dd>
                  </div>
                ) : null}
                {/* La provenance se lit des deux côtés : la fiche du test liste
                    ses études, l'étude nomme le test qui l'a déclenchée. */}
                {s.origin_test_id ? (
                  <div>
                    <dt>Issue du test</dt>
                    <dd>
                      <Link
                        to={`/projet/${encodeURIComponent(projectId)}/tests/${encodeURIComponent(s.origin_test_id)}`}
                        className="card-title-link"
                      >
                        {s.origin_test_code ?? s.origin_test_objective ?? s.origin_test_id}
                      </Link>
                    </dd>
                  </div>
                ) : null}
              </dl>
              {s.context ? <p className="card-note">{s.context}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </ListSection>
  );
}

// --- Tests utilisateurs ---------------------------------------------------

/** Seuils de lecture d'un taux de réussite, alignés sur le tableau de bord. */
function successTone(rate: number): string {
  if (rate >= 80) return 'tone-good';
  if (rate >= 60) return 'tone-warn';
  return 'tone-bad';
}

/** Où en est le scénario, en une pastille : c'est ce qui dit si le test a été
 *  PASSÉ, indépendamment de son statut déclaré. Un test « Terminé » dont aucune
 *  action n'est observée n'a pas eu lieu — la pastille le montre. */
function ScenarioChip({ test }: { test: UserTest }) {
  const total = test.scenario_total ?? 0;
  if (total === 0) return <Chip state="erreur">Scénario à écrire</Chip>;
  const observed = test.scenario_observed ?? 0;
  if (observed === 0) return <Chip state="avertissement">{total} action(s), rien d’observé</Chip>;
  if (observed < total) {
    return <Chip state="avertissement">{observed}/{total} observées</Chip>;
  }
  return <Chip state="succes">{total} action(s) observée(s)</Chip>;
}

export function TestsScreen({ projectId }: { projectId: string }) {
  const query = useTests(projectId);
  const canWrite = useCanWrite(projectId);
  const { navigate } = useRouter();
  const base = `/projet/${encodeURIComponent(projectId)}/tests`;
  return (
    <ListSection<UserTest>
      title="Tests utilisateurs"
      lede="Se cadrent d’abord, puis se passent : le scénario note action par action ce qui est arrivé, et c’est lui qui donne son résultat au test."
      query={query}
      actions={
        canWrite ? (
          <WriteAction
            label="Nouveau test"
            accent
            title="Cadrer un test utilisateur"
            description="D’abord ce que vous cherchez à trancher, et sur quoi. Personas, nombre de participants et scénario se renseignent ensuite, sur la fiche du test."
          >
            {(close) => (
              <TestForm
                projectId={projectId}
                onDone={close}
                // Créer, c'est ouvrir un chantier : on enchaîne sur la fiche,
                // là où s'écrit le scénario. Sans cela, le « deuxième temps »
                // resterait une intention.
                onCreated={(id) => navigate(`${base}/${encodeURIComponent(id)}`)}
              />
            )}
          </WriteAction>
        ) : null
      }
      emptyTitle="Aucun test pour l’instant"
      emptyDescription="Un test se cadre d’abord (objectif, type, prototype), puis on précise qui le passe, puis il se passe : une ligne par action, ce qui est arrivé, et le résultat se calcule tout seul."
    >
      {(tests) => (
        <div className="zy-table-cadre">
          <table className="zy-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Objectif</th>
                <th>Personas visés</th>
                <th className="num">Part.</th>
                <th className="num">Résultat</th>
                <th>Scénario</th>
                <th>Statut</th>
                {canWrite ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {tests.map((t) => (
                <TestRow key={t.id} test={t} projectId={projectId} base={base} canWrite={canWrite} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListSection>
  );
}

/** Le résultat CALCULÉ depuis le scénario : le taux, puis ce sur quoi il porte.
 *  « 72 % » sans « sur 3 actions observées » laisserait croire à une mesure
 *  d'ensemble alors que la moitié du tableau peut être encore vide. */
function ScenarioResult({ test }: { test: UserTest }) {
  if (test.scenario_score === null) {
    return (
      <>
        <span className="big-inline tone-empty">—</span>
        <span className="result-detail">rien d’observé</span>
      </>
    );
  }
  return (
    <>
      <span className={`big-inline ${successTone(test.scenario_score)}`}>
        {Math.round(test.scenario_score)}
        <span className="big-inline-unit">%</span>
      </span>
      <span className="result-detail">
        sur {test.scenario_observed} action{test.scenario_observed > 1 ? 's' : ''} observée
        {test.scenario_observed > 1 ? 's' : ''}
      </span>
    </>
  );
}

/** Deux pastilles de personas, puis « +N » — la liste complète en `title`.
 *  Voir QUI a été testé, pas seulement combien. */
function PersonaMarks({ personas }: { personas: UserTest['personas'] }) {
  if (personas.length === 0) return <span className="tone-empty">—</span>;
  const shown = personas.slice(0, 2);
  const rest = personas.length - shown.length;
  return (
    <span className="persona-marks" title={personas.map(personaLine).join(', ')}>
      {shown.map((p) => (
        <span
          key={p.id}
          className="persona-chip-mark"
          style={p.accent ? { background: p.accent } : undefined}
        >
          {p.initials ?? p.name.slice(0, 2).toUpperCase()}
        </span>
      ))}
      {rest > 0 ? <span className="persona-marks-more">+{rest}</span> : null}
    </span>
  );
}

function TestRow({
  test,
  projectId,
  base,
  canWrite,
}: {
  test: UserTest;
  projectId: string;
  base: string;
  canWrite: boolean;
}) {
  const { navigate } = useRouter();
  const to = `${base}/${encodeURIComponent(test.id)}`;

  // La ligne entière navigue — mais le VRAI lien reste dans la cellule
  // « Objectif ». Une <tr> ne peut pas être enveloppée dans une ancre, et lui
  // poser role="link" + tabIndex créerait un second point de tabulation par
  // ligne, annonçant tout son texte comme libellé de lien. Ceci n'est donc
  // qu'un confort de souris, qui s'efface devant tout le reste.
  const onRowClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if ((e.target as HTMLElement).closest('a,button,input,select,label,textarea')) return;
    // Sans ce garde-fou, sélectionner un mot dans une cellule navigue.
    if (window.getSelection()?.toString()) return;
    navigate(to);
  };

  return (
    <tr className="row-link" onClick={onRowClick}>
      <td className="t-mono">{test.code ?? test.id}</td>
      <td>
        <Link to={to} className="card-title-link">
          {test.objective ?? 'Sans objectif renseigné'}
        </Link>
        {test.type || test.period ? (
          <span className="row-sub">{[test.type, test.period].filter(Boolean).join(' · ')}</span>
        ) : null}
      </td>
      <td>
        <PersonaMarks personas={test.personas} />
      </td>
      <td className="num">{test.participants || '—'}</td>
      <td className="num result-cell">
        <ScenarioResult test={test} />
      </td>
      <td>
        <ScenarioChip test={test} />
      </td>
      <td>
        <StatusChip value={test.status} />
      </td>
      {canWrite ? (
        <td className="row-actions">
          <EditAction
            title="Modifier le cadrage"
            description="Objectif, type, personas visés, effectif. Le scénario et son résultat vivent sur la fiche."
          >
            {(close) => <TestForm projectId={projectId} existing={test} onDone={close} />}
          </EditAction>
          <DeleteTestAction projectId={projectId} test={test} />
        </td>
      ) : null}
    </tr>
  );
}

// --- Personas -------------------------------------------------------------

/** La fiche COMPLÈTE d'un persona, dans le panneau de droite. */
function PersonaSheet({ projectId, persona: p, canWrite }: { projectId: string; persona: Persona; canWrite: boolean }) {
  return (
    <div className="persona-sheet">
      <div className="persona-head">
        <Avatar initials={p.initials ?? initialsOf(p.name)} seed={p.id} size="m" />
        <div>
          <p className="persona-role">
            {[personaAside(p) ?? 'Rôle non renseigné', seniorityLabel(p.seniority), p.pole].filter(Boolean).join(' · ')}
          </p>
          <StatusChip value={p.status} />
        </div>
      </div>
      {p.description ? <p className="card-note">{p.description}</p> : null}
      <PersonaNeedsView needs={p.needs ?? []} />

      {/* LE TERRAIN SUR LEQUEL IL REPOSE. C'est ce qui distingue un persona
          observé d'un profil supposé. */}
      <div className="persona-studies">
        <Eyebrow>Issu de</Eyebrow>
        {p.studies.length === 0 ? (
          <Chip state="avertissement">Aucune étude rattachée</Chip>
        ) : (
          <span className="chips">
            {p.studies.map((s) => (
              <Link
                key={s.id}
                to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(s.id)}`}
                className={chipClass()}
              >
                {s.code ?? s.objective ?? s.id}
              </Link>
            ))}
          </span>
        )}
      </div>

      <div className="persona-stats">
        <span>
          <strong>{p.nugget_count ?? 0}</strong> faits
        </span>
        <span>
          <strong>{p.session_count ?? 0}</strong> sessions
        </span>
        {canWrite ? (
          <>
            <EditAction title="Modifier le persona">
              {(close) => <NewPersonaForm projectId={projectId} existing={p} onDone={close} />}
            </EditAction>
            <DeletePersonaAction projectId={projectId} persona={p} />
          </>
        ) : null}
      </div>
    </div>
  );
}

export function PersonasScreen({ projectId, tabs }: { projectId: string; tabs?: ReactNode }) {
  const query = usePersonas(projectId);
  const canWrite = useCanWrite(projectId);
  // Le persona dont la fiche est ouverte. On garde son IDENTIFIANT, pas l'objet :
  // après une modification, la fiche relit la version à jour ; après une
  // suppression, elle se referme d'elle-même.
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = (query.data ?? []).find((p) => p.id === openId) ?? null;
  const close = useCallback(() => setOpenId(null), []);
  return (
    <>
    <ListSection<Persona>
      title="Personas"
      lede="Les profils types auxquels les faits sont rattachés. Les vraies personnes sont dans l’annuaire."
      query={query}
      tabs={tabs}
      actions={
        canWrite ? (
          <WriteAction
            label="Nouveau persona"
            accent
            title="Décrire un persona"
            description="Sans profil, aucune lecture des résultats par type d’utilisateur n’est possible."
          >
            {(close) => <NewPersonaForm projectId={projectId} onDone={close} />}
          </WriteAction>
        ) : null
      }
      emptyTitle="Aucun persona décrit"
      emptyDescription="Sans profil, aucune lecture des résultats par type d’utilisateur n’est possible. C’est le seul socle exigé à l’unité pour construire le tableau d’adoption."
    >
      {(personas) => (
        <ul className="persona-grid">
          {personas.map((p) => (
            <li key={p.id}>
              {/* La carte ne montre que l'essentiel ; un clic ouvre la fiche
                  complète dans le panneau de droite. */}
              <button type="button" className="card persona persona-card" onClick={() => setOpenId(p.id)}>
                <span className="persona-head">
                  <Avatar initials={p.initials ?? initialsOf(p.name)} seed={p.id} size="m" />
                  <span>
                    <span className="t-h3 persona-card-title">{personaTitle(p)}</span>
                    <span className="persona-role">
                      {[personaAside(p) ?? 'Rôle non renseigné', seniorityLabel(p.seniority)].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </span>
                <span className="persona-card-foot">
                  <StatusChip value={p.status} />
                  <span className="persona-card-count">
                    {p.nugget_count ?? 0} fait{(p.nugget_count ?? 0) > 1 ? 's' : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </ListSection>
    <Drawer open={opened !== null} title={opened ? personaTitle(opened) : ''} onClose={close}>
      {opened ? <PersonaSheet projectId={projectId} persona={opened} canWrite={canWrite} /> : null}
    </Drawer>
    </>
  );
}

// --- Audits UX ------------------------------------------------------------

export function AuditsScreen({ projectId }: { projectId: string }) {
  const query = useAudits(projectId);
  const canWrite = useCanWrite(projectId);
  return (
    <ListSection<Audit>
      title="Audit UX"
      lede="Évaluations heuristiques d’un parcours, notées et commentées."
      query={query}
      actions={
        canWrite ? (
          <WriteAction
            label="Nouvel audit"
            accent
            title="Nouvel audit UX"
            description="Un audit évalue un parcours contre des heuristiques, sans utilisateur. Il complète le terrain, il ne le remplace pas."
          >
            {(close) => <AuditForm projectId={projectId} onDone={close} />}
          </WriteAction>
        ) : null
      }
      emptyTitle="Aucun audit pour l’instant"
      emptyDescription="Un audit évalue un parcours contre des heuristiques, sans utilisateur. Il complète le terrain, il ne le remplace pas."
    >
      {(audits) => (
        <ul className="card-list">
          {audits.map((a) => (
            <li key={a.id} className="card">
              <div className="card-head">
                <div>
                  <Eyebrow>{a.code ?? a.id}</Eyebrow>
                  <h2 className="t-h3">{a.title}</h2>
                </div>
                <div className="card-head-right">
                  <StatusChip value={a.status} />
                  {canWrite ? (
                    <EditAction title="Modifier l’audit">
                      {(close) => <AuditForm projectId={projectId} existing={a} onDone={close} />}
                    </EditAction>
                  ) : null}
                </div>
              </div>
              <dl className="facts">
                {a.version ? (
                  <div>
                    <dt>Version</dt>
                    <dd>{a.version}</dd>
                  </div>
                ) : null}
                {a.user_journey ? (
                  <div>
                    <dt>Parcours</dt>
                    <dd>{a.user_journey}</dd>
                  </div>
                ) : null}
                {a.audit_date ? (
                  <div>
                    <dt>Date</dt>
                    <dd>{a.audit_date}</dd>
                  </div>
                ) : null}
              </dl>
              {a.analysis_cats.length > 0 ? (
                <div className="chips">
                  {a.analysis_cats.map((c) => (
                    <TagChip key={c} tag={c} />
                  ))}
                </div>
              ) : null}
              {a.context ? <p className="card-note">{a.context}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </ListSection>
  );
}

// --- Triangulation --------------------------------------------------------

const CONCORDANCE_TONE: Record<string, string> = {
  concordant: 'tone-good',
  divergent: 'tone-bad',
  'à creuser': 'tone-warn',
};

function MeasureRow({
  row,
  projectId,
  triangulationId,
  canWrite,
}: {
  row: TriangulationRow;
  projectId: string;
  triangulationId: string;
  canWrite: boolean;
}) {
  return (
    <tr>
      {/* Le pilier RATTACHÉ fait autorité : c'est lui qui décide sur quelle
          note la ligne pèse. Le libellé libre n'est qu'un repère de saisie. */}
      <td>{row.pillar_name ?? row.pillar_label ?? '—'}</td>
      <td className="num">
        {/* Une mesure non saisie reste vide : `Number(null)` vaudrait zéro, et
            un zéro inventé fausserait la note du pilier. */}
        {row.score === null ? <span className="tone-empty">—</span> : row.score}
      </td>
      <td>{row.qual_summary ?? '—'}</td>
      <td>
        {/* LE CHIFFRE D'ABORD, l'interprétation ensuite. Une mesure sans valeur
            reste vide plutôt que d'afficher un zéro : ne pas avoir mesuré n'est
            pas avoir mesuré zéro. */}
        {row.metric_value === null ? (
          row.quant_summary ? null : <span className="tone-empty">—</span>
        ) : (
          <span className="measure-value">
            {row.metric_value}
            {row.metric_unit ? <span className="measure-unit">{row.metric_unit}</span> : null}
          </span>
        )}
        {row.metric_name ? <span className="measure-name">{row.metric_name}</span> : null}
        {row.quant_summary ? <span className="measure-read">{row.quant_summary}</span> : null}
        {row.source_tool || row.metric_period ? (
          <span className="source-tool">
            {[row.source_tool, row.metric_period].filter(Boolean).join(' · ')}
          </span>
        ) : null}
      </td>
      <td>
        {row.concordance ? (
          <Chip state={chipState(CONCORDANCE_TONE[row.concordance])}>
            {row.concordance}
          </Chip>
        ) : (
          '—'
        )}
      </td>
      {canWrite ? (
        <td className="row-actions">
          <EditAction title="Modifier la mesure">
            {(close) => (
              <TriangulationRowForm
                projectId={projectId}
                triangulationId={triangulationId}
                existing={row}
                onDone={close}
              />
            )}
          </EditAction>
        </td>
      ) : null}
    </tr>
  );
}

export function TriangulationScreen({ projectId }: { projectId: string }) {
  const query = useTriangulations(projectId);
  const { navigate } = useRouter();
  const canWrite = useCanWrite(projectId);
  // La liste des tests est DÉJÀ en cache (même projet) : on s'en sert pour
  // nommer les sources plutôt que d'afficher un identifiant technique.
  const tests = useTests(projectId);
  const testLabels = new Map(
    (tests.data ?? []).map((t) => [t.id, t.code ?? t.objective ?? t.id] as const),
  );
  // Même chose pour les études : « ÉTUDE-05 », pas leur identifiant.
  const studies = useStudies(projectId);
  const studyLabels = new Map(
    (studies.data ?? []).map((s) => [s.id, s.code ?? s.objective ?? s.id] as const),
  );
  const filter = useProjectFilter();
  return (
    <ListSection<import('../api/types').Triangulation>
      title="Triangulation"
      lede="Croisement du signal terrain et des données mesurées."
      query={query}
      filter={filter.matches}
      actions={
        <>
        {/* Les données mesurées viennent des connecteurs : on les règle ici. */}
        <Button
          variant="ghost"
          onClick={() => { presetTab('options', 'sources'); navigate(`/projet/${encodeURIComponent(projectId)}/options`); }}
        >
          Gérer les sources
        </Button>
        {canWrite ? (
          <WriteAction
            label="Nouveau croisement"
            accent
            title="Nouveau croisement"
            description="Posez le cadre d’un croisement. Les mesures s’ajoutent ensuite une à une, avec leur pilier."
          >
            {(close) => <NewTriangulationForm projectId={projectId} onDone={close} />}
          </WriteAction>
        ) : null}
        </>
      }
      emptyTitle="Aucune triangulation pour l’instant"
      emptyDescription="La triangulation confronte ce que les gens disent à ce que les outils mesurent (Google Analytics, Hotjar, Mixpanel…). Une divergence est une zone à creuser, pas une erreur."
    >
      {(triangulations) => (
        <div className="card-list">
          {triangulations.map((t) => (
            <section key={t.id} className="card">
              <div className="card-head">
                <div>
                  <Eyebrow>Croisement</Eyebrow>
                  <h2 className="t-h3">{t.objective ?? 'Sans objectif renseigné'}</h2>
                </div>
                <div className="card-head-right">
                  {t.connector_id ? <Chip>Import automatique</Chip> : null}
                  <Chip>
                    {t.rows.length} mesure{t.rows.length > 1 ? 's' : ''}
                  </Chip>
                  <LifecycleChip projectId={projectId} kind="triangulation" id={t.id} phase={t.phase} version={t.version} canWrite={canWrite} />
                </div>
              </div>
              {t.context ? <p className="card-note">{t.context}</p> : null}
              {/* D'où viennent les éléments croisés. Un test source devient un
                  lien : la mesure retourne à ce qui l'a produite. On affiche son
                  CODE, pas son identifiant — « TEST_c96a6d… » ne se lit pas. */}
              {(t.sources ?? []).length > 0 ? (
                <ul className="chips">
                  {(t.sources ?? []).map((s) => (
                    <li key={`${s.source_kind}:${s.source_ref}`}>
                      {s.source_kind === 'test' ? (
                        <Link
                          to={`/projet/${encodeURIComponent(projectId)}/tests/${encodeURIComponent(s.source_ref)}`}
                          className={chipClass()}
                        >
                          Test {testLabels.get(s.source_ref) ?? s.source_ref}
                        </Link>
                      ) : (
                        <Chip>
                          {s.source_kind === 'study' ? studyLabels.get(s.source_ref) ?? `Étude ${s.source_ref}` : `Source ${s.source_ref}`}
                        </Chip>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
              {t.rows.length > 0 ? (
                <div className="zy-table-cadre">
                  <table className="zy-table">
                    <thead>
                      <tr>
                        <th>Pilier</th>
                        <th className="num">Note</th>
                        <th>Signal terrain</th>
                        <th>Donnée mesurée</th>
                        <th>Concordance</th>
                        {canWrite ? <th aria-label="Actions" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {t.rows.map((r) => (
                        <MeasureRow
                          key={r.id}
                          row={r}
                          projectId={projectId}
                          triangulationId={t.id}
                          canWrite={canWrite}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {canWrite ? (
                <div className="card-foot">
                  <WriteAction
                    label="Ajouter une mesure"
                    title="Ajouter une mesure"
                    description="Croisez un signal terrain avec une donnée mesurée. Le pilier rattaché décide sur quelle note la mesure pèse."
                  >
                    {(close) => (
                      <TriangulationRowForm projectId={projectId} triangulationId={t.id} onDone={close} />
                    )}
                  </WriteAction>
                </div>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </ListSection>
  );
}
